import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import Filters from "../layout/Filters";
import {
  ArrowsRightLeftIcon,
  ClockIcon,
  CodeBracketSquareIcon,
  ExclamationCircleIcon,
  FlagIcon,
} from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import SwitchToggle from "../forms/SwitchToggle";
import Dropdown from "../forms/Dropdown";
import Indicator from "../layout/Indicator";
import DangerZone from "../forms/DangerZone";
import {
  createWebhook,
  deleteWebhooks,
  getWebhooks,
  updateWebhook,
} from "../../slices/integration";
import TextArea from "../forms/TextArea";
import {
  buildBodyPlaceholderLookup,
  buildBodyPlaceholders,
  buildUrlPlaceholders,
  formatBodyForForm,
} from "../../utils/placeholders";

export default function Configuration() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchAction = searchParams.get("action");
  const searchMethod = searchParams.get("method");

  // Fetch data from server
  useEffect(() => {
    dispatch(getWebhooks({ action: searchAction, method: searchMethod }));
  }, [dispatch, searchAction, searchMethod]);

  // Set state
  const [action, setAction] = useState({ value: "" });
  const [method, setMethod] = useState({ value: "" });
  const [selectedEditWebhook, setSelectedEditWebhook] = useState<{
    id: string;
    name: string;
    action: string;
    integration_entity_id: string;
    url: string;
    url_placeholders: { [key: string]: any };
    method: string;
    auth_type: string;
    auth_in: string;
    auth_key_name: string;
    auth_key: string;
    body: string;
    body_placeholders: { [key: string]: any };
    status: boolean;
  } | null>(null);
  const [showEditWebhookModal, setShowEditWebhookModal] =
    useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [createWebhookFormData, setCreateWebhookFormData] = useState<{
    name: string;
    action: string;
    integration_entity_id: string;
    url: string;
    url_placeholders: { [key: string]: any };
    method: string;
    auth_type: string;
    auth_in: string;
    auth_key_name: string;
    auth_key: string;
    body: string;
    body_placeholders: { [key: string]: any };
  }>({
    name: "",
    action: "FOOR",
    integration_entity_id: "",
    url: "",
    url_placeholders: {},
    method: "G",
    auth_type: "N",
    auth_in: "N",
    auth_key_name: "",
    auth_key: "",
    body: "{}",
    body_placeholders: {},
  });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { webhooks, options } = useAppSelector((state) => state.integration);
  const language = user === null ? "UK" : user.language;

  function selectWebhookToEdit(e: any, args: any[]) {
    e.preventDefault();
    if (webhooks.data === null) return;
    const [webhookId] = args;
    const webhookData = webhooks.data.filter(
      (hook) => hook.id === webhookId,
    )[0];

    const formattedBodyPlaceholders = formatBodyForForm(
      webhookData.placeholders.body,
    );
    setSelectedEditWebhook({
      id: webhookId,
      name: webhookData.name,
      action: webhookData.action[0],
      integration_entity_id: webhookData.integration_entity.id,
      url: webhookData.url,
      url_placeholders: webhookData.placeholders.url,
      method: webhookData.method[0],
      auth_type: webhookData.auth_type,
      auth_in: webhookData.auth_in,
      auth_key_name: webhookData.auth_key_name,
      auth_key: webhookData.auth_key,
      body: webhookData.body,
      body_placeholders: formattedBodyPlaceholders,
      status: webhookData.status[0] === "A",
    });
    setShowEditWebhookModal(true);
  }

  function openCreateWebhookModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  async function confirmCreateWebhook(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      action,
      integration_entity_id: chosenIntegrationEntity,
      url,
      url_placeholders: urlPlaceholders,
      method,
      auth_type: authType,
      auth_in: authIn,
      auth_key_name: authKeyName,
      auth_key: authKey,
      body,
      body_placeholders: bodyPlaceholders,
    } = createWebhookFormData;

    // Validate top level text fields
    if (name === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (url === "") {
      dispatch(
        addAlert(generateAlertText("missing_url", language), "danger", 5000),
      );
      return;
    }

    // Validate and parse URL and body
    let parsedBody: any = body.replaceAll("{{}}", "{}");
    parsedBody = JSON.parse(parsedBody);
    const cleanUrl = url.replaceAll("{{}}", "{}");
    const formattedUrlPlaceholders = buildUrlPlaceholders(
      cleanUrl,
      urlPlaceholders,
    );
    const formattedBodyPlaceholders = buildBodyPlaceholders(
      bodyPlaceholders,
      parsedBody,
    );
    const placeholders = {
      url:
        Object.keys(formattedUrlPlaceholders).length === 0
          ? null
          : formattedUrlPlaceholders,
      body:
        Object.keys(formattedBodyPlaceholders).length === 0
          ? null
          : formattedBodyPlaceholders,
    };

    setShowCreateModal(false);
    const res: any = await dispatch(
      createWebhook({
        name,
        action,
        property: user.property.id,
        integrationEntity: chosenIntegrationEntity,
        url: cleanUrl,
        method,
        authType,
        authIn,
        authKeyName,
        authKey,
        placeholders,
        body: parsedBody,
        status: "A",
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_webhook", language),
          "success",
          3000,
        ),
      );
      setCreateWebhookFormData({
        name: "",
        action: "FOOR",
        integration_entity_id: "",
        url: "",
        url_placeholders: {},
        method: "G",
        auth_type: "N",
        auth_in: "N",
        auth_key_name: "",
        auth_key: "",
        body: "{}",
        body_placeholders: {},
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditWebhook(e: any) {
    e.preventDefault();
    if (selectedEditWebhook === null) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      action,
      integration_entity_id: chosenIntegrationEntity,
      url,
      url_placeholders: urlPlaceholders,
      method,
      auth_type: authType,
      auth_in: authIn,
      auth_key_name: authKeyName,
      auth_key: authKey,
      body,
      body_placeholders: bodyPlaceholders,
      status,
    } = selectedEditWebhook;

    // Validate top level text fields
    if (name === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (url === "") {
      dispatch(
        addAlert(generateAlertText("missing_url", language), "danger", 5000),
      );
      return;
    }

    // Validate and parse URL and body
    let parsedBody: any = body.replaceAll("{{}}", "{}");
    parsedBody = JSON.parse(parsedBody);
    const cleanUrl = url.replaceAll("{{}}", "{}");
    const formattedUrlPlaceholders = buildUrlPlaceholders(
      cleanUrl,
      urlPlaceholders,
    );
    const formattedBodyPlaceholders = buildBodyPlaceholders(
      bodyPlaceholders,
      parsedBody,
    );
    const placeholders = {
      url:
        Object.keys(formattedUrlPlaceholders).length === 0
          ? null
          : formattedUrlPlaceholders,
      body:
        Object.keys(formattedBodyPlaceholders).length === 0
          ? null
          : formattedBodyPlaceholders,
    };

    setShowEditWebhookModal(false);
    const res: any = await dispatch(
      updateWebhook({
        webhookId: id,
        name,
        action,
        property: user.property.id,
        integrationEntity: chosenIntegrationEntity,
        url: cleanUrl,
        method,
        authType,
        authIn,
        authKeyName,
        authKey,
        placeholders,
        body: parsedBody,
        status: status ? "A" : "I",
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_webhook", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditWebhookModal(true);
    }
  }

  async function confirmDeleteWebhook(e: any, args: any[]) {
    e.preventDefault();
    const [webhookIds] = args;
    if (webhookIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (webhookIds === null || webhookIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_webhooks", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditWebhookModal(false);
    const res: any = await dispatch(
      deleteWebhooks({ webhookIds: [webhookIds] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditWebhookModal(true);
    }
  }

  const webhooksData = webhooks.data === null ? [] : webhooks.data;
  const webhookActionChoices =
    options.actions === undefined
      ? []
      : Object.keys(options.actions).map((key) => [
          key,
          options.actions[key][language],
        ]);
  const webhookMethodChoices: Array<[string, string]> =
    options.methods === undefined ? [] : options.methods;
  const webhookAuthTypeChoices =
    options.auth_type === undefined
      ? []
      : Object.keys(options.auth_type).map((key) => [
          key,
          options.auth_type[key][language],
        ]);
  const webhookAuthInChoices =
    options.auth_in === undefined
      ? []
      : Object.keys(options.auth_in).map((key) => [
          key,
          options.auth_in[key][language],
        ]);
  const webhookAuthKeyNameChoices: Array<[string, string]> =
    options.auth_key_name === undefined ? [] : options.auth_key_name;
  const webhookPlaceholdersChoices =
    options.placeholders === undefined
      ? []
      : Object.keys(options.placeholders).map((key) => [
          key,
          options.placeholders[key][language],
        ]);
  const webhookEntityChoices: Array<[string, string]> =
    options.integration_entities === undefined
      ? []
      : options.integration_entities;
  const webhookActionOptions =
    options.actions === undefined
      ? []
      : Object.keys(options.actions).map((key) => ({
          value: key,
          label: options.actions[key][language],
        }));
  const webhookMethodOptions =
    options.methods === undefined
      ? []
      : options.methods.map((x: any) => ({ value: x[0], label: x[1] }));
  const placeholderToken = "{{}}";
  const createNumUrlPlaceholders: Array<number> = Array.from(
    { length: createWebhookFormData.url.split(placeholderToken).length - 1 },
    (x, i) => i,
  );
  const {
    parsedBodyValid: createParsedBodyValid,
    lookup: createBodyPlaceholderLookup,
  } = buildBodyPlaceholderLookup(createWebhookFormData.body, placeholderToken);
  const {
    parsedBodyValid: updateParsedBodyValid,
    lookup: updateBodyPlaceholderLookup,
  } = buildBodyPlaceholderLookup(
    selectedEditWebhook === null ? {} : selectedEditWebhook.body,
    placeholderToken,
  );
  const updateNumUrlPlaceholders: Array<number> =
    selectedEditWebhook === null
      ? []
      : Array.from(
          {
            length: selectedEditWebhook.url.split(placeholderToken).length - 1,
          },
          (x, i) => i,
        );

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("webhooks_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateWebhookModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_webhook", language)}
              </Button>
            }
          />
        }
      />

      {/* Create webhook modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_webhook", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateWebhook}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_webhook", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createWebhookFormData}
                stateName="name"
                onChange={setCreateWebhookFormData}
                placeholder={translateText(
                  "name_placeholder_webhook",
                  language,
                )}
              />

              {/* Trigger action */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("trigger_action", language)}
                showLabel={true}
                required={true}
                state={createWebhookFormData}
                stateName="action"
                onChange={setCreateWebhookFormData}
                defaultValue="FOOR"
              >
                {webhookActionChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Destination system */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("destination", language)}
                showLabel={true}
                required={true}
                state={createWebhookFormData}
                stateName="integration_entity_id"
                onChange={setCreateWebhookFormData}
                defaultValue=""
              >
                <option value=""></option>
                {webhookEntityChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* URL */}
              <TextInput
                id="url"
                type="text"
                label={translateText("url", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createWebhookFormData}
                stateName="url"
                onChange={setCreateWebhookFormData}
                placeholder={translateText("url_placeholder_webhook", language)}
                small={translateText("url_subtext_webhook", language)}
              />

              {/* Dynamic placeholders for URL */}
              {createNumUrlPlaceholders.length > 0 && (
                <>
                  {createNumUrlPlaceholders.map((num) => (
                    <div key={num}>
                      <label
                        htmlFor={`url-placeholder-${num}`}
                        className={`mb-3 block text-left text-sm font-medium text-black`}
                      >
                        {translateText(
                          "placeholder___for_url",
                          language,
                        ).replace(placeholderToken, num + 1)}
                      </label>
                      <div className="col-span-full">
                        <select
                          id={`url-placeholder-${num}`}
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          required={true}
                          onChange={(e) => {
                            const currentPlaceholders =
                              createWebhookFormData.url_placeholders;
                            const { value } = e.target;
                            const name = num.toString();
                            if (!(name in currentPlaceholders))
                              currentPlaceholders[name] = null;
                            currentPlaceholders[name] = value;
                            setCreateWebhookFormData({
                              ...createWebhookFormData,
                              url_placeholders: currentPlaceholders,
                            });
                          }}
                          defaultValue={
                            num in createWebhookFormData.url_placeholders
                              ? createWebhookFormData.url_placeholders[num]
                              : ""
                          }
                        >
                          {webhookPlaceholdersChoices.map((opt) => (
                            <option key={opt[0]} value={opt[0]}>
                              {opt[1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* HTTP method */}
              <Dropdown
                id="method"
                className="col-span-full"
                label={translateText("method", language)}
                showLabel={true}
                required={true}
                state={createWebhookFormData}
                stateName="method"
                onChange={setCreateWebhookFormData}
                defaultValue="G"
              >
                {webhookMethodChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Authentication type */}
              <Dropdown
                id="auth_type"
                className="col-span-full"
                label={translateText("authentication_type", language)}
                showLabel={true}
                required={true}
                state={createWebhookFormData}
                stateName="auth_type"
                onChange={setCreateWebhookFormData}
                defaultValue="N"
              >
                {webhookAuthTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Authentication in */}
              {createWebhookFormData.auth_type !== "N" && (
                <Dropdown
                  id="auth_in"
                  className="col-span-full"
                  label={translateText("authentication_in", language)}
                  showLabel={true}
                  required={true}
                  state={createWebhookFormData}
                  stateName="auth_in"
                  onChange={setCreateWebhookFormData}
                  defaultValue="N"
                >
                  {webhookAuthInChoices.map((opt) => (
                    <option key={opt[0]} value={opt[0]}>
                      {opt[1]}
                    </option>
                  ))}
                </Dropdown>
              )}

              {/* Authentication key name */}
              {createWebhookFormData.auth_type !== "N" && (
                <Dropdown
                  id="auth_key_name"
                  className="col-span-full"
                  label={translateText("authentication_key_name", language)}
                  showLabel={true}
                  required={true}
                  state={createWebhookFormData}
                  stateName="auth_key_name"
                  onChange={setCreateWebhookFormData}
                  defaultValue="N"
                >
                  {webhookAuthKeyNameChoices.map((opt) => (
                    <option key={opt[0]} value={opt[0]}>
                      {opt[1]}
                    </option>
                  ))}
                </Dropdown>
              )}

              {/* Authentication key name */}
              {createWebhookFormData.auth_type !== "N" && (
                <TextInput
                  id="auth_key"
                  type="text"
                  label={translateText("auth_key", language)}
                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                  required={true}
                  showLabel={true}
                  state={createWebhookFormData}
                  stateName="auth_key"
                  onChange={setCreateWebhookFormData}
                  placeholder={translateText(
                    "auth_key_placeholder_webhook",
                    language,
                  )}
                  small={translateText("auth_key_subtext_webhook", language)}
                />
              )}

              {/* Body */}
              {createWebhookFormData.method !== "G" && (
                <TextArea
                  id="body"
                  label={translateText("body", language)}
                  className={
                    createParsedBodyValid === true
                      ? "block w-full appearance-none rounded-md border border-success bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-success focus:bg-light/40 focus:outline-none focus:ring-success sm:text-sm"
                      : "block w-full appearance-none rounded-md border border-danger bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-danger focus:bg-light/40 focus:outline-none focus:ring-danger sm:text-sm"
                  }
                  required={true}
                  showLabel={true}
                  state={createWebhookFormData}
                  stateName="body"
                  onChange={setCreateWebhookFormData}
                  placeholder={translateText(
                    "body_placeholder_webhook",
                    language,
                  )}
                  small={translateText("body_subtext_webhook", language)}
                  rows={8}
                />
              )}

              {/* Dynamic placeholders for body */}
              {createBodyPlaceholderLookup.length > 0 &&
                createBodyPlaceholderLookup.map((x: [string, any]) =>
                  Array.from({ length: x[1] }, (x, i) => i).map((y: any) => (
                    <div key={`body-placeholder-${x[0]}-${y}`}>
                      <label
                        htmlFor={`body-placeholder-${x[0]}-${y}`}
                        className={`mb-3 block text-left text-sm font-medium text-black`}
                      >
                        {translateText("placeholder___for_body", language)
                          .replace("||", y + 1)
                          .replace(placeholderToken, x[0])}
                      </label>
                      <div className="col-span-full">
                        <select
                          id={`body-placeholder-${x[0]}-${y}`}
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          required={true}
                          onChange={(e) => {
                            const currentPlaceholders =
                              createWebhookFormData.body_placeholders;
                            const { value } = e.target;
                            const name = `${y}_${x[0]}`;
                            if (!(name in currentPlaceholders))
                              currentPlaceholders[name] = null;
                            currentPlaceholders[name] = value;
                            setCreateWebhookFormData({
                              ...createWebhookFormData,
                              body_placeholders: currentPlaceholders,
                            });
                          }}
                          defaultValue={
                            `${y}_${x[0]}` in
                            createWebhookFormData.body_placeholders
                              ? createWebhookFormData.body_placeholders[
                                  `${y}_${x[0]}`
                                ]
                              : ""
                          }
                        >
                          {webhookPlaceholdersChoices.map((opt) => (
                            <option key={opt[0]} value={opt[0]}>
                              {opt[1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )),
                )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit webhook modal */}
      <Modal
        show={showEditWebhookModal}
        setShow={setShowEditWebhookModal}
        confirmText={translateText("edit_webhook", language)}
        cancelText="Cancel"
        confirmHandler={confirmEditWebhook}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_webhook", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedEditWebhook}
                stateName="name"
                onChange={setSelectedEditWebhook}
              />

              {/* Trigger action */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("trigger_action", language)}
                showLabel={true}
                required={true}
                state={selectedEditWebhook}
                stateName="action"
                onChange={setSelectedEditWebhook}
                defaultValue={
                  selectedEditWebhook === null
                    ? "FOOR"
                    : selectedEditWebhook.action
                }
              >
                {webhookActionChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Destination system */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("destination", language)}
                showLabel={true}
                required={true}
                state={selectedEditWebhook}
                stateName="integration_entity_id"
                onChange={setSelectedEditWebhook}
                defaultValue={
                  selectedEditWebhook === null
                    ? ""
                    : selectedEditWebhook.integration_entity_id
                }
              >
                <option value=""></option>
                {webhookEntityChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* URL */}
              <TextInput
                id="url"
                type="text"
                label={translateText("url", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedEditWebhook}
                stateName="url"
                onChange={setSelectedEditWebhook}
                placeholder={translateText("url_placeholder_webhook", language)}
                small={translateText("url_subtext_webhook", language)}
              />

              {/* Dynamic placeholders for URL */}
              {updateNumUrlPlaceholders.length > 0 && (
                <>
                  {updateNumUrlPlaceholders.map((num) => (
                    <div key={num}>
                      <label
                        htmlFor={`url-placeholder-${num}`}
                        className={`mb-3 block text-left text-sm font-medium text-black`}
                      >
                        {translateText(
                          "placeholder___for_url",
                          language,
                        ).replace(placeholderToken, num + 1)}
                      </label>
                      <div className="col-span-full">
                        <select
                          id={`url-placeholder-${num}`}
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          required={true}
                          onChange={(e) => {
                            if (selectedEditWebhook === null) return;
                            const currentPlaceholders: any =
                              selectedEditWebhook.url_placeholders;
                            let updatedPlaceholders = {
                              ...currentPlaceholders,
                            };
                            const { value } = e.target;
                            const name = num.toString();
                            if (!(name in updatedPlaceholders))
                              updatedPlaceholders[name] = null;
                            updatedPlaceholders[name] = value;
                            setSelectedEditWebhook({
                              ...selectedEditWebhook,
                              url_placeholders: updatedPlaceholders,
                            });
                          }}
                          defaultValue={
                            selectedEditWebhook === null
                              ? ""
                              : num in selectedEditWebhook.url_placeholders
                              ? selectedEditWebhook.url_placeholders[num]
                              : ""
                          }
                        >
                          {webhookPlaceholdersChoices.map((opt) => (
                            <option key={opt[0]} value={opt[0]}>
                              {opt[1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* HTTP method */}
              <Dropdown
                id="method"
                className="col-span-full"
                label={translateText("method", language)}
                showLabel={true}
                required={true}
                state={selectedEditWebhook}
                stateName="method"
                onChange={setSelectedEditWebhook}
                defaultValue={
                  selectedEditWebhook === null
                    ? "G"
                    : selectedEditWebhook.method
                }
              >
                {webhookMethodChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Authentication type */}
              <Dropdown
                id="auth_type"
                className="col-span-full"
                label={translateText("authentication_type", language)}
                showLabel={true}
                required={true}
                state={selectedEditWebhook}
                stateName="auth_type"
                onChange={setSelectedEditWebhook}
                defaultValue={
                  selectedEditWebhook === null
                    ? "N"
                    : selectedEditWebhook.auth_type
                }
              >
                {webhookAuthTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Authentication in */}
              {selectedEditWebhook !== null &&
                selectedEditWebhook.auth_type !== "N" && (
                  <Dropdown
                    id="auth_in"
                    className="col-span-full"
                    label={translateText("authentication_in", language)}
                    showLabel={true}
                    required={true}
                    state={selectedEditWebhook}
                    stateName="auth_in"
                    onChange={setSelectedEditWebhook}
                    defaultValue={
                      selectedEditWebhook === null
                        ? "N"
                        : selectedEditWebhook.auth_in
                    }
                  >
                    {webhookAuthInChoices.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>
                )}

              {/* Authentication key name */}
              {selectedEditWebhook !== null &&
                selectedEditWebhook.auth_type !== "N" && (
                  <Dropdown
                    id="auth_key_name"
                    className="col-span-full"
                    label={translateText("authentication_key_name", language)}
                    showLabel={true}
                    required={true}
                    state={selectedEditWebhook}
                    stateName="auth_key_name"
                    onChange={setSelectedEditWebhook}
                    defaultValue={
                      selectedEditWebhook === null
                        ? "N"
                        : selectedEditWebhook.auth_key_name
                    }
                  >
                    {webhookAuthKeyNameChoices.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>
                )}

              {/* Authentication key name */}
              {selectedEditWebhook !== null &&
                selectedEditWebhook.auth_type !== "N" && (
                  <TextInput
                    id="auth_key"
                    type="text"
                    label={translateText("auth_key", language)}
                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                    required={true}
                    showLabel={true}
                    state={selectedEditWebhook}
                    stateName="auth_key"
                    onChange={setSelectedEditWebhook}
                    placeholder={translateText(
                      "auth_key_placeholder_webhook",
                      language,
                    )}
                    small={translateText("auth_key_subtext_webhook", language)}
                  />
                )}

              {/* Body */}
              {selectedEditWebhook !== null &&
                selectedEditWebhook.method !== "G" && (
                  <TextArea
                    id="body"
                    label={translateText("body", language)}
                    className={
                      updateParsedBodyValid === true
                        ? "block w-full appearance-none rounded-md border border-success bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-success focus:bg-light/40 focus:outline-none focus:ring-success sm:text-sm"
                        : "block w-full appearance-none rounded-md border border-danger bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-danger focus:bg-light/40 focus:outline-none focus:ring-danger sm:text-sm"
                    }
                    required={true}
                    showLabel={true}
                    state={selectedEditWebhook}
                    stateName="body"
                    onChange={setSelectedEditWebhook}
                    placeholder={translateText(
                      "body_placeholder_webhook",
                      language,
                    )}
                    small={translateText("body_subtext_webhook", language)}
                    rows={8}
                  />
                )}

              {/* Dynamic placeholders for body */}
              {updateBodyPlaceholderLookup.length > 0 &&
                updateBodyPlaceholderLookup.map((x: [string, any]) =>
                  Array.from({ length: x[1] }, (x, i) => i).map((y: any) => (
                    <div key={`body-placeholder-${x[0]}-${y}`}>
                      <label
                        htmlFor={`body-placeholder-${x[0]}-${y}`}
                        className={`mb-3 block text-left text-sm font-medium text-black`}
                      >
                        {translateText("placeholder___for_body", language)
                          .replace("||", y + 1)
                          .replace(placeholderToken, x[0])}
                      </label>
                      <div className="col-span-full">
                        <select
                          id={`body-placeholder-${x[0]}-${y}`}
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          required={true}
                          onChange={(e) => {
                            if (selectedEditWebhook === null) return;
                            const currentPlaceholders =
                              selectedEditWebhook.body_placeholders;
                            let updatedPlaceholders = {
                              ...currentPlaceholders,
                            };
                            const { value } = e.target;
                            const name = `${y}_${x[0]}`;
                            if (!(name in updatedPlaceholders))
                              updatedPlaceholders[name] = null;
                            updatedPlaceholders[name] = value;
                            setSelectedEditWebhook({
                              ...selectedEditWebhook,
                              body_placeholders: updatedPlaceholders,
                            });
                          }}
                          defaultValue={
                            selectedEditWebhook === null
                              ? ""
                              : `${y}_${x[0]}` in
                                selectedEditWebhook.body_placeholders
                              ? selectedEditWebhook.body_placeholders[
                                  `${y}_${x[0]}`
                                ]
                              : ""
                          }
                        >
                          {webhookPlaceholdersChoices.map((opt) => (
                            <option key={opt[0]} value={opt[0]}>
                              {opt[1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )),
                )}

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                showLabel={true}
                description={translateText("status_webhook_subtext", language)}
                toggle={
                  selectedEditWebhook === null
                    ? false
                    : selectedEditWebhook.status
                }
                state={selectedEditWebhook}
                stateName="status"
                handler={setSelectedEditWebhook}
              />

              {/* Danger zone */}
              {selectedEditWebhook !== null && (
                <DangerZone
                  objectId={selectedEditWebhook.id}
                  handler={confirmDeleteWebhook}
                  language={language}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {webhooks.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "action",
                  name: "Action",
                  options: webhookActionOptions,
                },
                {
                  id: "method",
                  name: "Method",
                  options: webhookMethodOptions,
                },
              ]}
              queryParams={[
                ["action", searchAction],
                ["method", searchMethod],
              ]}
              handlers={{
                action: {
                  state: action,
                  stateName: "value",
                  onChange: setAction,
                },
                method: {
                  state: method,
                  stateName: "value",
                  onChange: setMethod,
                },
              }}
              loading={webhooks.loading}
              baseUrl="/integration/configuration"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {webhooksData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {webhooksData.map((hook) => {
                    return (
                      <a
                        key={hook.id}
                        href="#!"
                        onClick={(e) => selectWebhookToEdit(e, [hook.id])}
                        className="relative"
                      >
                        <Card
                          className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                        >
                          {/* Card header */}
                          <div className={`border-b border-light pb-2`}>
                            <div className="items-center justify-between">
                              <div>
                                <h3
                                  className={`text-base font-semibold leading-6 text-black truncate`}
                                >
                                  {hook.name}
                                </h3>
                              </div>
                            </div>
                          </div>

                          <div className="pt-2 grid grid-cols-1 space-y-2">
                            <div className="inline-flex text-sm text-secondary">
                              <ArrowsRightLeftIcon
                                className="h-5 w-5 text-info mr-1"
                                aria-hidden="true"
                              />
                              {hook.integration_entity.name}
                            </div>
                            <div className="inline-flex text-sm text-secondary">
                              <FlagIcon
                                className="h-5 w-5 text-info mr-1"
                                aria-hidden="true"
                              />
                              {hook.action[1]}
                            </div>
                            <div className="inline-flex text-sm text-secondary">
                              <CodeBracketSquareIcon
                                className="h-5 w-5 text-info mr-1"
                                aria-hidden="true"
                              />
                              {hook.method[1]}
                            </div>
                            <div className="inline-flex text-sm text-secondary">
                              <ClockIcon
                                className="h-5 w-5 text-secondary mr-1"
                                aria-hidden="true"
                              />
                              {hook.datetime_created}
                            </div>
                          </div>
                        </Card>

                        {/* Status indicator */}
                        <Indicator
                          size={3}
                          colour={
                            hook.status[0] === "A" ? "success" : "warning"
                          }
                          pulse={false}
                        />
                      </a>
                    );
                  })}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_webhooks", language)}
                subtext={translateText("no_webhooks_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
